import React, { useState, useEffect } from 'react';
import { X, Package, MapPin, Calendar, FileText, CheckCircle, Loader2, Download } from 'lucide-react';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useLocationFilter } from '@/contexts/LocationFilterContext';
import { locationService, Location } from '@/firebase/locationService';
import { complianceService, ComplianceTemplate } from '@/firebase/complianceService';
import { 
  getPoliciesByJurisdiction,
  getUniqueJurisdictions,
  generateAuditExportPreview, 
  createAuditPackage,
  type AuditExportRequest,
  type AuditPackageOptions 
} from '@/services/auditExportService';
import { useNotification } from '@/contexts/NotificationContext';

interface AuditExportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type Step = 'scope' | 'policies' | 'review' | 'exporting' | 'complete';

const DATE_RANGE_OPTIONS = [
  { value: 'last_30_days', label: 'Last 30 days' },
  { value: 'last_90_days', label: 'Last 90 days' },
  { value: 'last_12_months', label: 'Last 12 months' },
  { value: 'all_time', label: 'All time' },
  { value: 'custom', label: 'Custom range' },
];

const AuditExportModal: React.FC<AuditExportModalProps> = ({ isOpen, onClose }) => {
  const { currentOrganization } = useOrganization();
  const { availableLocations } = useLocationFilter();
  const { showNotification } = useNotification();
  
  const [step, setStep] = useState<Step>('scope');
  const [isLoading, setIsLoading] = useState(false);
  
  // Step 1: Scope
  const [exportName, setExportName] = useState<string>('');
  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  const [selectedJurisdictions, setSelectedJurisdictions] = useState<string[]>([]);
  const [availableJurisdictions, setAvailableJurisdictions] = useState<string[]>([]);
  const [isLoadingJurisdictions, setIsLoadingJurisdictions] = useState(false);
  const [dateRange, setDateRange] = useState<AuditExportRequest['dateRange']>({
    type: 'last_12_months',
  });
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  
  // Step 2: Policies
  const [availablePolicies, setAvailablePolicies] = useState<ComplianceTemplate[]>([]);
  const [selectedPolicyIds, setSelectedPolicyIds] = useState<Set<string>>(new Set());
  const [isLoadingPolicies, setIsLoadingPolicies] = useState(false);
  
  // Step 3: Review
  const [preview, setPreview] = useState<any>(null);
  const [options, setOptions] = useState<AuditPackageOptions>({
    includeSummary: true,
    includeMetadata: true,
    includeAuditTrail: true,
    includeScores: false,
  });
  
  // Step 4: Export
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [packageId, setPackageId] = useState<string | null>(null);
  
  // Load available jurisdictions when modal opens
  useEffect(() => {
    if (isOpen && currentOrganization?.id && step === 'scope') {
      loadJurisdictions();
    }
  }, [isOpen, currentOrganization?.id, step]);
  
  // Load policies when scope changes
  useEffect(() => {
    if (step === 'policies' && selectedJurisdictions.length > 0 && currentOrganization?.id) {
      loadPolicies();
    }
  }, [step, selectedJurisdictions, selectedLocationId, currentOrganization?.id]);
  
  const loadJurisdictions = async () => {
    if (!currentOrganization?.id) return;
    
    setIsLoadingJurisdictions(true);
    try {
      const jurisdictions = await getUniqueJurisdictions(currentOrganization.id);
      setAvailableJurisdictions(jurisdictions);
    } catch (error) {
      console.error('Error loading jurisdictions:', error);
      showNotification('Failed to load jurisdictions', 'error');
    } finally {
      setIsLoadingJurisdictions(false);
    }
  };
  
  const loadPolicies = async () => {
    if (!currentOrganization?.id) return;
    
    setIsLoadingPolicies(true);
    try {
      const policies = await getPoliciesByJurisdiction(
        currentOrganization.id,
        selectedJurisdictions,
        selectedLocationId ? [selectedLocationId] : undefined
      );
      setAvailablePolicies(policies);
    } catch (error) {
      console.error('Error loading policies:', error);
      showNotification('Failed to load policies', 'error');
    } finally {
      setIsLoadingPolicies(false);
    }
  };
  
  const handleNextFromScope = () => {
    if (!exportName.trim()) {
      showNotification('Please enter an export name', 'error');
      return;
    }
    if (!selectedLocationId) {
      showNotification('Please select a location', 'error');
      return;
    }
    if (selectedJurisdictions.length === 0) {
      showNotification('Please select at least one jurisdiction', 'error');
      return;
    }
    
    // Handle custom date range
    if (dateRange?.type === 'custom') {
      if (!customStartDate || !customEndDate) {
        showNotification('Please select both start and end dates for custom range', 'error');
        return;
      }
      setDateRange({
        type: 'custom',
        startDate: customStartDate,
        endDate: customEndDate,
      });
    }
    
    setStep('policies');
  };
  
  const handleNextFromPolicies = () => {
    if (selectedPolicyIds.size === 0) {
      showNotification('Please select at least one policy', 'error');
      return;
    }
    setStep('review');
    generatePreview();
  };
  
  const generatePreview = async () => {
    if (!currentOrganization?.id) return;
    
    setIsLoading(true);
    try {
      const request: AuditExportRequest = {
        exportName,
        locationIds: selectedLocationId ? [selectedLocationId] : [],
        jurisdictions: selectedJurisdictions,
        policyIds: Array.from(selectedPolicyIds),
        dateRange,
        organizationId: currentOrganization.id,
      };
      
      const previewResult = await generateAuditExportPreview(request);
      setPreview(previewResult);
      setPackageId(previewResult.packageId);
    } catch (error) {
      console.error('Error generating preview:', error);
      showNotification('Failed to generate preview', 'error');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleExport = async () => {
    if (!currentOrganization?.id || !packageId) return;
    
    setStep('exporting');
    try {
      const request: AuditExportRequest = {
        exportName,
        locationIds: selectedLocationId ? [selectedLocationId] : [],
        jurisdictions: selectedJurisdictions,
        policyIds: Array.from(selectedPolicyIds),
        dateRange,
        organizationId: currentOrganization.id,
      };
      
      const result = await createAuditPackage(packageId, request, options);
      setDownloadUrl(result.downloadUrl);
      setStep('complete');
    } catch (error) {
      console.error('Error creating package:', error);
      showNotification('Failed to create export package', 'error');
      setStep('review');
    }
  };
  
  const handleClose = () => {
    // Reset state
    setStep('scope');
    setExportName('');
    setSelectedLocationId('');
    setSelectedJurisdictions([]);
    setDateRange({ type: 'last_12_months' });
    setAvailablePolicies([]);
    setSelectedPolicyIds(new Set());
    setPreview(null);
    setDownloadUrl(null);
    setPackageId(null);
    onClose();
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <Package className="w-6 h-6 text-primary-600" />
            <h2 className="text-2xl font-semibold text-gray-900">Audit Export</h2>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {step === 'scope' && (
            <div className="space-y-6">
              {isLoadingJurisdictions ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary-600 mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Loading Audit Export</h3>
                  <p className="text-sm text-gray-600">Preparing your compliance data...</p>
                </div>
              ) : (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Step 1: Select Audit Scope</h3>
                
                {/* Export Name */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Export Name *
                  </label>
                  <input
                    type="text"
                    value={exportName}
                    onChange={(e) => setExportName(e.target.value)}
                    placeholder="e.g., Liquor License Audit - Q1 2024"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <p className="mt-1 text-xs text-gray-500">Give this export a descriptive name for easy identification</p>
                </div>
                
                {/* Location */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <MapPin className="w-4 h-4 inline mr-1" />
                    Location *
                  </label>
                  <select
                    value={selectedLocationId}
                    onChange={(e) => setSelectedLocationId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">Select a location</option>
                    {availableLocations.map(location => (
                      <option key={location.id} value={location.id}>
                        {location.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Jurisdictions */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FileText className="w-4 h-4 inline mr-1" />
                    Jurisdictions *
                  </label>
                  {isLoadingJurisdictions ? (
                    <div className="flex items-center justify-center py-4 border border-gray-300 rounded-md">
                      <Loader2 className="w-4 h-4 animate-spin text-primary-600 mr-2" />
                      <span className="text-sm text-gray-600">Loading jurisdictions...</span>
                    </div>
                  ) : availableJurisdictions.length === 0 ? (
                    <div className="border border-gray-300 rounded-md p-4 text-center text-sm text-gray-500">
                      No jurisdictions found. Please create compliance templates first.
                    </div>
                  ) : (
                    <div className="border border-gray-300 rounded-md p-3">
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                        {availableJurisdictions.map(jurisdiction => (
                          <label key={jurisdiction} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                            <input
                              type="checkbox"
                              checked={selectedJurisdictions.includes(jurisdiction)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedJurisdictions([...selectedJurisdictions, jurisdiction]);
                                } else {
                                  setSelectedJurisdictions(selectedJurisdictions.filter(j => j !== jurisdiction));
                                }
                              }}
                              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            <span className="text-sm text-gray-700">{jurisdiction}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Date Range */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    Date Range
                  </label>
                  <select
                    value={dateRange?.type || 'last_12_months'}
                    onChange={(e) => {
                      const type = e.target.value as 'custom' | 'last_30_days' | 'last_90_days' | 'last_12_months' | 'all_time';
                      if (type === 'custom') {
                        setDateRange({ type: 'custom' });
                      } else {
                        setDateRange({ type });
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    {DATE_RANGE_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  
                  {dateRange?.type === 'custom' && (
                    <div className="mt-3 grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Start Date</label>
                        <input
                          type="date"
                          value={customStartDate}
                          onChange={(e) => setCustomStartDate(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">End Date</label>
                        <input
                          type="date"
                          value={customEndDate}
                          onChange={(e) => setCustomEndDate(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
              )}
            </div>
          )}
          
          {step === 'policies' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Step 2: Select Compliance Items</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Policies matching: <span className="font-medium">{selectedJurisdictions.join(', ')}</span>
                </p>
                
                {isLoadingPolicies ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
                    <span className="ml-2 text-sm text-gray-600">Loading policies...</span>
                  </div>
                ) : availablePolicies.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p>No policies found for selected jurisdictions.</p>
                    <button
                      onClick={() => setStep('scope')}
                      className="mt-4 text-sm text-primary-600 hover:text-primary-700"
                    >
                      ← Back to scope selection
                    </button>
                  </div>
                ) : (
                  <div className="border border-gray-300 rounded-md overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-3 text-left">
                            <input
                              type="checkbox"
                              checked={selectedPolicyIds.size === availablePolicies.length && availablePolicies.length > 0}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedPolicyIds(new Set(availablePolicies.map(p => p.id!)));
                                } else {
                                  setSelectedPolicyIds(new Set());
                                }
                              }}
                              className="rounded border-gray-300 text-primary-600"
                            />
                          </th>
                          <th className="px-4 py-3 text-left font-semibold text-gray-700">Policy Name</th>
                          <th className="px-4 py-3 text-left font-semibold text-gray-700">Jurisdiction</th>
                          <th className="px-4 py-3 text-left font-semibold text-gray-700">Category</th>
                          <th className="px-4 py-3 text-left font-semibold text-gray-700">Risk</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {availablePolicies.map(policy => (
                          <tr key={policy.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <input
                                type="checkbox"
                                checked={selectedPolicyIds.has(policy.id!)}
                                onChange={(e) => {
                                  const newSet = new Set(selectedPolicyIds);
                                  if (e.target.checked) {
                                    newSet.add(policy.id!);
                                  } else {
                                    newSet.delete(policy.id!);
                                  }
                                  setSelectedPolicyIds(newSet);
                                }}
                                className="rounded border-gray-300 text-primary-600"
                              />
                            </td>
                            <td className="px-4 py-3 text-gray-900">{policy.name}</td>
                            <td className="px-4 py-3 text-gray-600">{policy.jurisdiction}</td>
                            <td className="px-4 py-3 text-gray-600">{policy.category}</td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                policy.risk_level === 'High' ? 'bg-red-100 text-red-800' :
                                policy.risk_level === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-green-100 text-green-800'
                              }`}>
                                {policy.risk_level}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {step === 'review' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Step 3: Review & Customize</h3>
                
                {isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
                    <span className="ml-2 text-sm text-gray-600">Generating preview...</span>
                  </div>
                ) : preview ? (
                  <>
                    <div className="bg-gray-50 rounded-lg p-4 mb-6">
                      <h4 className="font-semibold text-gray-900 mb-3">Export Details</h4>
                      <div className="mb-3">
                        <span className="text-gray-600 text-sm">Export Name:</span>
                        <span className="ml-2 font-medium text-gray-900">{exportName}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Documents:</span>
                          <span className="ml-2 font-medium text-gray-900">{preview.documentCount}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Locations:</span>
                          <span className="ml-2 font-medium text-gray-900">{preview.locations.length}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Date Range:</span>
                          <span className="ml-2 font-medium text-gray-900">{preview.dateRange}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Estimated Size:</span>
                          <span className="ml-2 font-medium text-gray-900">{preview.estimatedSize}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mb-6">
                      <h4 className="font-semibold text-gray-900 mb-3">Export Options</h4>
                      <div className="space-y-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={options.includeSummary}
                            onChange={(e) => setOptions({ ...options, includeSummary: e.target.checked })}
                            className="rounded border-gray-300 text-primary-600"
                          />
                          <span className="text-sm text-gray-700">Include Summary Report</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={options.includeMetadata}
                            onChange={(e) => setOptions({ ...options, includeMetadata: e.target.checked })}
                            className="rounded border-gray-300 text-primary-600"
                          />
                          <span className="text-sm text-gray-700">Include Metadata Spreadsheet</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={options.includeAuditTrail}
                            onChange={(e) => setOptions({ ...options, includeAuditTrail: e.target.checked })}
                            className="rounded border-gray-300 text-primary-600"
                          />
                          <span className="text-sm text-gray-700">Include Audit Trail</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={options.includeScores}
                            onChange={(e) => setOptions({ ...options, includeScores: e.target.checked })}
                            className="rounded border-gray-300 text-primary-600"
                          />
                          <span className="text-sm text-gray-700">Include Compliance Scores</span>
                        </label>
                      </div>
                    </div>
                  </>
                ) : null}
              </div>
            </div>
          )}
          
          {step === 'exporting' && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-12 h-12 animate-spin text-primary-600 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Generating Package...</h3>
              <p className="text-sm text-gray-600">This may take a few minutes</p>
            </div>
          )}
          
          {step === 'complete' && downloadUrl && (
            <div className="flex flex-col items-center justify-center py-12">
              <CheckCircle className="w-16 h-16 text-green-500 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Package Ready!</h3>
              <p className="text-sm text-gray-600 mb-6">Your audit package has been generated.</p>
              <button
                onClick={() => {
                  if (downloadUrl.startsWith('data:') || downloadUrl.startsWith('blob:')) {
                    // Handle data URL or blob URL
                    const link = document.createElement('a');
                    link.href = downloadUrl;
                    link.download = `${exportName || 'audit-export'}.zip`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    // Clean up blob URL after a delay
                    if (downloadUrl.startsWith('blob:')) {
                      setTimeout(() => URL.revokeObjectURL(downloadUrl), 100);
                    }
                  } else {
                    // Regular URL - open in new tab
                    window.open(downloadUrl, '_blank');
                  }
                }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                <Download className="w-5 h-5" />
                Download Package
              </button>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="border-t p-6 flex items-center justify-between">
          {step === 'scope' && (
            <>
              <div></div>
              <button
                onClick={handleNextFromScope}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                Next: Select Policies
              </button>
            </>
          )}
          
          {step === 'policies' && (
            <>
              <button
                onClick={() => setStep('scope')}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleNextFromPolicies}
                disabled={selectedPolicyIds.size === 0}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next: Review & Export
              </button>
            </>
          )}
          
          {step === 'review' && (
            <>
              <button
                onClick={() => setStep('policies')}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleExport}
                disabled={isLoading}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Generate Package
              </button>
            </>
          )}
          
          {step === 'complete' && (
            <button
              onClick={handleClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors ml-auto"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditExportModal;

